import React, { useMemo, useState } from 'react';
import './AgendaPanel.css';

const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

const buildLocalDateTime = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map((val) => parseInt(val, 10));
  const [hour, minute] = (timeStr || '00:00').split(':').map((val) => parseInt(val, 10));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, hour || 0, minute || 0);
};

const getScrimStart = (scrim) => {
  if (!scrim) return null;
  if (scrim.allDay) {
    return buildLocalDateTime(scrim.startDate, '00:00');
  }
  return (
    toDate(scrim.startAt) ||
    buildLocalDateTime(scrim.startDate, scrim.startTime || '00:00')
  );
};

const getScrimEnd = (scrim) => {
  if (!scrim) return null;
  if (scrim.allDay) {
    return buildLocalDateTime(scrim.endDate, '23:59');
  }
  return (
    toDate(scrim.endAt) ||
    buildLocalDateTime(scrim.endDate, scrim.endTime || scrim.startTime || '00:00')
  );
};

const formatDateHeading = (date) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);

const formatTime = (date) =>
  new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);

const formatRelative = (date, now) => {
  if (!date) return '';
  const diffMs = date.getTime() - now.getTime();
  if (diffMs <= 0) return 'Starting now';
  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `in ${minutes} min`;
  if (minutes === 0) return `in ${hours} hr`;
  return `in ${hours} hr ${minutes} min`;
};

const formatTimeRange = (scrim) => {
  if (scrim.allDay) return 'All day';
  if (scrim._start && scrim._end) {
    return `${formatTime(scrim._start)} - ${formatTime(scrim._end)}`;
  }
  return 'Time TBD';
};

const formatTimeRemaining = (scrim, now) => {
  if (!scrim._end) return 'Time remaining: --';
  const diffMs = scrim._end.getTime() - now.getTime();
  const totalMinutes = Math.round(diffMs / 60000);
  if (totalMinutes <= 0) return 'Time elapsed';
  if (totalMinutes < 60) return `Time remaining: ${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `Time remaining: ${hours} hr ${minutes} min`;
};

const buildQuickMessage = (scrim, modePref) => {
  const modeLabel = modePref === 'Any' ? 'a set' : `2 ${modePref}s`;
  const planned = scrim.plannedContent || scrim.mapPool || '';
  const plannedSuffix = planned ? ` (${planned})` : '';
  return `Scrim just cancelled - can you run ${modeLabel} now${plannedSuffix}?`;
};

const isSameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const AgendaPanel = ({
  scrims = [],
  now = new Date(),
  onSelectScrim,
  onCancelScrim,
  onUpdateScrim,
  onReplaceScrim,
  onRecoveryAction,
}) => {
  const [showTodayDrawer, setShowTodayDrawer] = useState(false);
  const [replacementOpenId, setReplacementOpenId] = useState(null);
  const [replacementDrafts, setReplacementDrafts] = useState({});
  const [cancelReasonDrafts, setCancelReasonDrafts] = useState({});

  const scrimsWithTimes = useMemo(() => {
    return scrims
      .map((scrim) => ({
        ...scrim,
        _start: getScrimStart(scrim),
        _end: getScrimEnd(scrim),
      }))
      .filter((item) => item._start)
      .sort((a, b) => a._start - b._start);
  }, [scrims]);

  const activeScrims = useMemo(
    () => scrimsWithTimes.filter((item) => !item.cancelled),
    [scrimsWithTimes]
  );

  const nowScrim = useMemo(() => {
    return activeScrims.find((item) => {
      if (!item._start || !item._end) return false;
      if (item.allDay) return isSameDay(item._start, now);
      return item._start <= now && item._end > now;
    });
  }, [activeScrims, now]);

  const nextScrim = useMemo(() => {
    return activeScrims.find((item) => item._start > now) || null;
  }, [activeScrims, now]);

  const todayScrims = useMemo(() => {
    return scrimsWithTimes.filter((item) => isSameDay(item._start, now));
  }, [scrimsWithTimes, now]);

  const opponentCounts = useMemo(() => {
    const counts = {};
    scrims.forEach((scrim) => {
      const name = scrim.history?.opponent || scrim.opponent;
      if (!name) return;
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [scrims]);

  const suggestedOpponents = (scrim) => {
    const excluded = new Set([
      scrim.opponent,
      scrim.history?.opponent,
      scrim.replacementOpponent,
    ].filter(Boolean));
    return Object.entries(opponentCounts)
      .filter(([name]) => !excluded.has(name))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name]) => name);
  };

  const getReplacementDraft = (scrim) =>
    replacementDrafts[scrim.id] || {
      modePref: 'HP',
      message: buildQuickMessage(scrim, 'HP'),
      replacementOpponent: '',
    };

  const setReplacementDraft = (scrimId, updates) => {
    setReplacementDrafts((prev) => ({
      ...prev,
      [scrimId]: {
        ...prev[scrimId],
        ...updates,
      },
    }));
  };

  const handleCopy = async (message) => {
    if (!message) return;
    try {
      await navigator.clipboard.writeText(message);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = message;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  const renderCancelledCard = (scrim) => {
    const reasonValue =
      cancelReasonDrafts[scrim.id] ?? scrim.cancelReason ?? '';
    const originalOpponent = scrim.history?.opponent || scrim.opponent || 'TBD';
    const planned = scrim.plannedContent || scrim.mapPool || 'Not set';
    const replacementDraft = getReplacementDraft(scrim);
    const suggestions = suggestedOpponents(scrim);
    return (
      <div className="scrim-card cancelled" key={scrim.id}>
        <div className="scrim-card-title">Scrim Cancelled</div>
        <div className="scrim-card-time">{formatTimeRange(scrim)}</div>
        <div className="scrim-card-row">
          <span className="label">Original Opponent:</span>
          <span>{originalOpponent}</span>
        </div>
        <div className="scrim-card-row">
          <span className="label">Planned:</span>
          <span>{planned}</span>
        </div>
        <div className="scrim-card-row">
          <span className="label">Cancel Reason:</span>
          <input
            type="text"
            value={reasonValue}
            placeholder="Optional"
            onChange={(e) =>
              setCancelReasonDrafts((prev) => ({
                ...prev,
                [scrim.id]: e.target.value,
              }))
            }
            onBlur={() => {
              if (onUpdateScrim && reasonValue !== scrim.cancelReason) {
                onUpdateScrim(scrim, { cancelReason: reasonValue });
              }
            }}
          />
        </div>
        <div className="scrim-card-divider"></div>
        <div className="scrim-card-actions">
          <div className="scrim-card-actions-title">What now?</div>
          <div className="scrim-card-buttons">
            <button
              type="button"
              className="primary"
              onClick={() =>
                setReplacementOpenId((prev) => (prev === scrim.id ? null : scrim.id))
              }
            >
              Find Replacement
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() =>
                onRecoveryAction && onRecoveryAction(scrim, 'Fill with ranked')
              }
            >
              Fill with Ranked
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() =>
                onRecoveryAction && onRecoveryAction(scrim, 'End session early')
              }
            >
              End Session Early
            </button>
          </div>
        </div>
        {replacementOpenId === scrim.id && (
          <div className="scrim-replacement">
            <div className="scrim-replacement-title">Find Replacement Set</div>
            <div className="scrim-replacement-section">
              <span>Suggested (based on history):</span>
              <ul>
                {suggestions.length === 0 && <li>No suggestions yet.</li>}
                {suggestions.map((name) => (
                  <li key={name}>
                    <button
                      type="button"
                      className={
                        replacementDraft.replacementOpponent === name ? 'active' : ''
                      }
                      onClick={() =>
                        setReplacementDraft(scrim.id, {
                          replacementOpponent: name,
                        })
                      }
                    >
                      {name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="scrim-replacement-section">
              <span>Mode Preference:</span>
              <div className="scrim-mode-toggle">
                {['HP', 'S&D', 'Any'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={
                      replacementDraft.modePref === mode ? 'active' : ''
                    }
                    onClick={() =>
                      setReplacementDraft(scrim.id, {
                        modePref: mode,
                        message: buildQuickMessage(scrim, mode),
                      })
                    }
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>
            <div className="scrim-replacement-section">
              <span>Quick Message:</span>
              <textarea
                value={replacementDraft.message}
                onChange={(e) =>
                  setReplacementDraft(scrim.id, { message: e.target.value })
                }
              />
            </div>
            <div className="scrim-replacement-actions">
              <button
                type="button"
                className="secondary"
                onClick={() => handleCopy(replacementDraft.message)}
              >
                Copy Message
              </button>
              <button
                type="button"
                className="primary"
                onClick={() => {
                  if (replacementDraft.replacementOpponent && onReplaceScrim) {
                    onReplaceScrim(scrim, replacementDraft);
                  }
                  setReplacementOpenId(null);
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}
        <div className="scrim-card-footer">{formatTimeRemaining(scrim, now)}</div>
      </div>
    );
  };

  const renderReplacedCard = (scrim) => {
    const originalOpponent = scrim.history?.opponent || scrim.opponent || 'TBD';
    return (
      <div className="scrim-card replaced" key={scrim.id}>
        <div className="scrim-card-title">Scrim Replaced</div>
        <div className="scrim-card-time">{formatTimeRange(scrim)}</div>
        <div className="scrim-card-row">
          <span className="label">Cancelled:</span>
          <span>{originalOpponent}</span>
        </div>
        <div className="scrim-card-row">
          <span className="label">Replacement:</span>
          <span>{scrim.replacementOpponent || 'TBD'}</span>
        </div>
        <div className="scrim-card-row">
          <span className="label">Actual:</span>
          <span>{scrim.actualContent || scrim.plannedContent || 'Not set'}</span>
        </div>
        <div className="scrim-card-row">
          <span className="label">Notes:</span>
          <span>{scrim.replacementNotes || 'Last-minute fill'}</span>
        </div>
      </div>
    );
  };

  const renderTodayItem = (item) => {
    if (item.replaced) return renderReplacedCard(item);
    if (item.cancelled) return renderCancelledCard(item);
    return (
      <div
        key={item.id}
        className="agenda-item"
        role="button"
        tabIndex={0}
        onClick={() => onSelectScrim && onSelectScrim(item)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSelectScrim && onSelectScrim(item);
          }
        }}
      >
        <div className="agenda-item-title">{item.title || 'Untitled event'}</div>
        <div className="agenda-item-meta">
          {!item.allDay && item._start ? formatTime(item._start) : 'All day'}
          {item.format ? ` - ${item.format}` : ''}
          {onCancelScrim && (
            <span className="agenda-item-cancel">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelScrim(item, '');
                }}
              >
                Cancel
              </button>
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="agenda-panel agenda-rail">
      <div className="agenda-header">
        <div>
          <h3>Agenda</h3>
          <p>What's next for me?</p>
        </div>
      </div>

      <div className="agenda-rail-block">
        <div className="agenda-rail-label now">Now</div>
        {nowScrim ? (
          <div className="agenda-rail-card">
            <span className="agenda-status-dot live" aria-hidden="true"></span>
            <div className="agenda-rail-title">{nowScrim.title || 'Untitled event'}</div>
            <div className="agenda-rail-time">{formatTimeRange(nowScrim)}</div>
            <button
              type="button"
              className="agenda-rail-action"
              onClick={() => onSelectScrim && onSelectScrim(nowScrim)}
            >
              Join
            </button>
          </div>
        ) : (
          <div className="agenda-rail-empty"><span aria-hidden="true"></span>No live scrim.</div>
        )}
      </div>

      <div className="agenda-rail-block">
        <div className="agenda-rail-label next">Next</div>
        {nextScrim ? (
          <div className="agenda-rail-card">
            <span className="agenda-status-dot next" aria-hidden="true"></span>
            <div className="agenda-rail-title">{nextScrim.title || 'Untitled event'}</div>
            <div className="agenda-rail-time">{formatTimeRange(nextScrim)}</div>
            <div className="agenda-rail-sub">{formatRelative(nextScrim._start, now)}</div>
            <div className="agenda-rail-actions">
              <button
                type="button"
                className="agenda-rail-action secondary"
                onClick={() => onSelectScrim && onSelectScrim(nextScrim)}
              >
                View
              </button>
              {onCancelScrim && (
                <button
                  type="button"
                  className="agenda-rail-link"
                  onClick={() => onCancelScrim(nextScrim, '')}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="agenda-rail-empty"><span aria-hidden="true"></span>No upcoming scrim.</div>
        )}
      </div>

      <button
        type="button"
        className="agenda-view-today"
        onClick={() => setShowTodayDrawer(true)}
      >
        View Today
      </button>

      {showTodayDrawer && (
        <div className="agenda-drawer-overlay" onClick={() => setShowTodayDrawer(false)}>
          <div
            className="agenda-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="agenda-drawer-header">
              <div>
                <h4>Today's Schedule</h4>
                <p>{formatDateHeading(now)}</p>
              </div>
              <button
                type="button"
                className="agenda-drawer-close"
                onClick={() => setShowTodayDrawer(false)}
              >
                Close
              </button>
            </div>
            <div className="agenda-drawer-list">
              {todayScrims.length === 0 && (
                <div className="agenda-empty">No scrims today.</div>
              )}
              {todayScrims.map((item) => renderTodayItem(item))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendaPanel;
